import { PREDICT_LABEL_TEXT, trendResolutionDetail } from "../lib/barrierCopy";
import { challengeDate, resolveChallengeOrder } from "../lib/dailyChallenge";
import { resolveRound } from "../lib/resolve";
import { matchesFilter, sharedDailySource, type RoundFilter } from "../lib/rounds";
import type { BinaryLabel, Preset, PresetResolution, Round } from "../types";
import { useChallengeRunStore } from "./challengeRunStore";
import type { HistoryEntryInput } from "./historyStore";
import { createRoundLoop } from "./roundLoop";

/**
 * The daily challenge Loop (GRA-139): the trend game over ten Rounds the date
 * alone decides, played in sequence and ending in a result screen. Another
 * `createRoundLoop` instance (ADR-0008), using its finite-run mode: the
 * lifecycle sequences the Rounds, lib/dailyChallenge picks them, and
 * challengeRunStore keeps the progress and the day's result.
 *
 * Everything a player could otherwise tune is fixed here, because a shared
 * puzzle has to be the same puzzle: the difficulty preset (so a Round
 * resolves the same way for everybody), the Context length (so everybody sees
 * the same chart), and the market filter (which is simply not applied). Those
 * settings keep whatever the player chose for free play and come back
 * untouched when they switch out of the challenge.
 */

/** The preset every challenge Round resolves at, whatever the player's setting. */
export const CHALLENGE_PRESET: Preset = "normal";

/** The Context length every challenge chart shows, whatever the player's setting. */
export const CHALLENGE_CONTEXT_LENGTH = 50;

/**
 * Which Rounds the challenge may deal (GRA-145). The same eligibility free
 * play's trend Loop applies (`trendRoundFilter`), minus the settings a shared
 * puzzle cannot honour: no market filter, no Context-length filter, and the
 * fixed preset rather than the player's. What is left is the one requirement
 * the rule on screen makes: the Round has to resolve by a barrier hit, so
 * "which barrier price touches first" is always a question the Round answers.
 */
export function challengeRoundFilter(): RoundFilter {
  return { barrierPreset: CHALLENGE_PRESET };
}

/** What the challenge reveals: the resolution it was scored against. */
export type ChallengeReveal = { resolution: PresetResolution };

/** Resolve a challenge commitment at the fixed preset. */
export function resolveChallenge(
  round: Round,
  guess: BinaryLabel,
): { reveal: ChallengeReveal; correct: boolean } {
  const resolution = resolveRound(round, CHALLENGE_PRESET);
  return { reveal: { resolution }, correct: guess === resolution.label };
}

/**
 * Challenge Rounds are logged as the Predict trend Rounds they are (GRA-123),
 * tagged with the run they belong to (GRA-154): a recorded attempt counts
 * towards the player's own numbers, a practice replay does not, and the
 * entry says which it was rather than leaving the two indistinguishable.
 */
function historyChallenge(
  round: Round,
  guess: BinaryLabel,
  reveal: ChallengeReveal,
  correct: boolean | undefined,
  note: string | undefined,
): HistoryEntryInput {
  return {
    mode: "predict",
    timeframe: "daily",
    horizon: "trend",
    kind: useChallengeRunStore.getState().practice ? "practice" : "challenge",
    roundId: round.id,
    preset: CHALLENGE_PRESET,
    contextLength: CHALLENGE_CONTEXT_LENGTH,
    instrumentId: round.instrumentId,
    instrumentName: round.instrumentName,
    dateRange: round.dateRange,
    choice: PREDICT_LABEL_TEXT[guess],
    outcome: correct ? "correct" : "incorrect",
    detail: trendResolutionDetail(reveal.resolution),
    note,
  };
}

export const useChallengeStore = createRoundLoop<Round, BinaryLabel, ChallengeReveal>({
  openSource: sharedDailySource,
  // A key of its own: the challenge must not narrow what free play serves
  // next, nor be narrowed by it.
  seenKey: "seen:challenge",
  // Never consulted in run mode; the day's seed is the whole selection.
  filter: () => ({}),
  resolve: resolveChallenge,
  history: historyChallenge,
  analytics: {
    mode: "predict",
    timeframe: "daily",
    horizon: "trend",
    // Every challenge Round resolves at the same fixed preset, not whatever
    // free play has set (see CHALLENGE_PRESET above).
    preset: () => CHALLENGE_PRESET,
  },
  run: {
    // The whole run is decided from the index (GRA-152): the ranking needs
    // nothing but ids and the barrier outcome, both of which the index
    // carries, so the ten Rounds' candles are the only thing ever fetched.
    order: (index) => {
      // The date is read here, at hydrate, and nowhere else: that is what
      // pins a run to the day it started on (ADR-0009's rollover rule).
      const date = challengeDate();
      const eligible = index.filter((entry) => matchesFilter(entry, challengeRoundFilter()));
      // Last resort, mirroring selectNextRound's: a bank with no barrier-hit
      // Round at all is a broken bank, and serving something beats serving a
      // blank screen. The real bank is nowhere near it.
      const pool = eligible.length > 0 ? eligible : index;
      const run = useChallengeRunStore.getState();
      // Pinned ids first, so a bank redeployed mid-run cannot move the Rounds
      // the player has left (ADR-0009).
      const { ids } = resolveChallengeOrder(pool, date, run.pinnedIds(date));
      run.open(date, ids);
      return ids;
    },
    resumeAt: () => useChallengeRunStore.getState().outcomes.length,
    record: (index, correct) => useChallengeRunStore.getState().record(index, correct),
  },
});

/** Replay the open day for practice, from Round one. */
export function replayChallenge(): void {
  useChallengeRunStore.getState().replay();
  useChallengeStore.getState().restart();
}
